import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { batchUploadAudiosToOss, batchUploadImagesToOss, batchUploadVideosToOss } from '../../api/agiBackUpload';
import {
  createVirtualPortrait,
  createVirtualPortraitGroup,
  deleteVirtualPortrait,
  deleteVirtualPortraitGroup,
  listVirtualPortraits,
} from '../../api/virtualPortrait';
import { getUserAvatarAssetGroup } from '../../api/userAvatarAssetGroup';
import { useSeedanceLibraryStore, type LibraryGroup } from '../../store/seedanceLibraryStore';
import type { Asset, MediaType } from '../../store/seedanceTypes';
import { useSeedanceUiStore } from '../../store/seedanceUiStore';
import {
  LIBRARY_PORTRAIT_LIST_PAGE_SIZE,
  VP_ASSET_TYPE_AUDIO,
  VP_ASSET_TYPE_IMAGE,
  VP_ASSET_TYPE_VIDEO,
  VIRTUAL_PORTRAIT_PROJECT,
} from '../../utils/seedanceConstants';
import { assetNameMatchesSearch, mapVirtualPortraitApiListToAssets } from '../../utils/seedancePortraitUtils';
import { enrichPortraitAssetsMissingUrls } from '../../utils/enrichPortraitAssetUrls';
import {
  computeLibraryDrawerPortraitInitialPageSize,
  computeLibraryDrawerPortraitLoadMorePageSize,
  computeLibraryPickerPortraitInitialPageSize,
  computeLibraryPickerPortraitLoadMorePageSize,
  computeLibraryPortraitInitialPageSize,
  computeLibraryPortraitLoadMorePageSize,
} from '../../utils/libraryPortraitPaging';
import {
  computeAdaptiveBackfillPageSize,
  LIBRARY_SPARSE_BACKFILL_MAX_PAGES,
  shouldContinueSparseBackfill,
} from '../../utils/libraryPortraitSparseLoad';
import type { LibraryMediaFilter } from '../../utils/libraryPortraitSparseLoad';
import {
  fillMissingPreviews,
  getLibraryAssetPreviewKey,
  loadLibraryAssetPreviewCache,
  loadLibraryGroupAssetsSnapshot,
  prepareLibraryInitialPreviewState,
  persistLibraryAssetPreviewCache,
  persistLibraryGroupAssetsSnapshot,
} from '../../utils/seedanceLibraryAssetsSnapshot';
import {
  backfillWorkspaceNodesFromPreviewSources,
  resolveAssetForWorkspace as resolveAssetForWorkspaceFromCache,
} from '../../utils/seedanceWorkspaceMatRefresh';
import {
  buildVirtualAvatarGroupOptions,
  pickInitialVirtualAvatarGroupId,
  resolveVirtualAvatarGroupSelection,
  type VirtualAvatarGroupOption,
} from '../../utils/avatarAssetGroup';
import { isAbortLike } from '../../utils/seedanceRequestUtils';
import {
  isLibraryAudioFile,
  LIBRARY_AUDIO_ACCEPT,
  validateLibraryAudioFiles,
} from '../../utils/libraryAudioUpload';
import {
  bindRealPersonGroupForUser,
  parseRealPortraitCallbackParams,
  pollRealPortraitGroupId,
  startRealPortraitAuthSession,
  stripRealPortraitCallbackParams,
} from '../../utils/realPortraitAuth';
import {
  getLocalStorageValue,
  REAL_PORTRAIT_PENDING_BYTED_TOKEN_KEY,
  removeLocalStorageValue,
  SEEDANCE_STORAGE_KEYS,
  setLocalStorageValue,
} from '../../utils/seedanceStorage';
import { persistSeedanceWorkspaceState, useSeedanceWorkspaceStore } from '../../store/seedanceWorkspaceStore';
import { AssetCard } from '../../components/AssetCard';
import { AudioAssetCard } from '../../components/AudioAssetCard';
import { VideoAssetCard } from '../../components/VideoAssetCard';
import { useRealPersonLibrary } from './useRealPersonLibrary';
import './library.css';

interface UseLibraryPageControllerArgs {
  active: boolean;
  /** 虚拟人像：素材组接口完成后再拉列表；为 false 时不 abort 进行中的列表请求 */
  portraitListEnabled?: boolean;
  hasTaskListConfig: () => boolean;
  /** 主素材库页 / 右侧抽屉 / 选择器弹窗，影响首屏条数估算 */
  pagingLayout?: 'page' | 'drawer' | 'picker';
  /** 滚动容器（抽屉须传入 `.lib-drawer-body`） */
  scrollRootRef?: React.RefObject<HTMLDivElement | null>;
  /** 下拉加载哨兵元素 */
  loadMoreSentinelRef?: React.RefObject<HTMLDivElement | null>;
  /** 为 true 时 active 且已有列表数据则跳过首屏重载（抽屉复用主列表缓存） */
  skipInitialLoadIfPopulated?: boolean;
  /** 为 false 时不注册列表拉取/分页 effect（如仅复用 CRUD 的弹窗层，避免 abort 主列表 session） */
  portraitListManaged?: boolean;
  /** 主素材库页类型筛选；非 all 时会一次会话内补拉剩余分页 */
  portraitMediaFilter?: LibraryMediaFilter;
}

type PortraitPagingSession = {
  fetchSeq: number;
  page: number;
  pageSize: number;
  loadedGroupId: string;
  loadedSearchName: string;
  listAbort: AbortController | null;
  cachedGroupId: string;
  cachedAssets: Asset[];
};

/** 主页面分页游标 */
const libPortraitPagingSession: PortraitPagingSession = {
  fetchSeq: 0,
  page: 0,
  pageSize: LIBRARY_PORTRAIT_LIST_PAGE_SIZE,
  loadedGroupId: '',
  loadedSearchName: '',
  listAbort: null,
  cachedGroupId: '',
  cachedAssets: [],
};
/** 侧边栏抽屉独立分页游标 */
const drawerPagingSession: PortraitPagingSession = {
  fetchSeq: 0,
  page: 0,
  pageSize: LIBRARY_PORTRAIT_LIST_PAGE_SIZE,
  loadedGroupId: '',
  loadedSearchName: '',
  listAbort: null,
  cachedGroupId: '',
  cachedAssets: [],
};
/** 素材选择器弹窗独立分页游标 */
const pickerPagingSession: PortraitPagingSession = {
  fetchSeq: 0,
  page: 0,
  pageSize: LIBRARY_PORTRAIT_LIST_PAGE_SIZE,
  loadedGroupId: '',
  loadedSearchName: '',
  listAbort: null,
  cachedGroupId: '',
  cachedAssets: [],
};

function abortPortraitListSession(session: PortraitPagingSession) {
  session.listAbort?.abort();
  session.listAbort = null;
}

/** 取消上一次列表请求并开启新序号（避免切换组后旧响应把 loading 卡住） */
function startPortraitListFetch(session: PortraitPagingSession) {
  abortPortraitListSession(session);
  session.fetchSeq += 1;
  const seq = session.fetchSeq;
  const controller = new AbortController();
  session.listAbort = controller;
  return { seq, signal: controller.signal };
}

function mergeSessionPreviewAssets(existing: Asset[], incoming: Asset[]): Asset[] {
  const byIdentity = new Map<string, Asset>();
  [...existing, ...incoming].forEach((asset) => {
    const sourceId = (asset.sourceAssetId || asset.id || '').trim();
    if (!sourceId) return;
    const key = getLibraryAssetPreviewKey(asset.type, sourceId);
    const previous = byIdentity.get(key);
    byIdentity.set(key, {
      ...previous,
      ...asset,
      url: asset.url || previous?.url || null,
      thumb: asset.thumb || previous?.thumb || null,
    });
  });
  return Array.from(byIdentity.values());
}

function libraryAssetMatchesIdentity(a: Asset, sourceAssetId: string, localId: string): boolean {
  return a.id === localId || (sourceAssetId !== '' && a.sourceAssetId === sourceAssetId);
}

/** 素材库页删除后，同步移除工作台选择器/抽屉中的同一条素材。 */
function removeLibraryAssetFromSharedStores(asset: Asset): void {
  const sourceAssetId = asset.sourceAssetId?.trim() ?? '';
  const localId = asset.id;
  const keep = (x: Asset) => !libraryAssetMatchesIdentity(x, sourceAssetId, localId);

  const store = useSeedanceLibraryStore.getState();
  const nextRemoteAssets = store.libRemoteAssets.filter(keep);
  store.setLibRemoteAssets(nextRemoteAssets);
  store.setLibPickerRemoteAssets((prev) => prev.filter(keep));
  store.setLibDrawerRemoteAssets((prev) => prev.filter(keep));

  pickerPagingSession.loadedGroupId = '';
  drawerPagingSession.loadedGroupId = '';

  const groupId = store.libSelectedGroupId.trim();
  if (groupId) {
    void persistLibraryGroupAssetsSnapshot(groupId, nextRemoteAssets);
  }
}

/** 素材库模块业务逻辑：加载素材组、素材列表、上传/删除素材。 */
export const useLibraryPageController = ({
  active,
  portraitListEnabled = true,
  hasTaskListConfig,
  pagingLayout = 'page',
  scrollRootRef: scrollRootRefProp,
  loadMoreSentinelRef: loadMoreSentinelRefProp,
  skipInitialLoadIfPopulated = false,
  portraitListManaged = true,
  portraitMediaFilter = 'all',
}: UseLibraryPageControllerArgs) => {
  const setNodes = useSeedanceWorkspaceStore((s) => s.setNodes);
  const addMatNode = useSeedanceWorkspaceStore((s) => s.addMatNode);
  const applyLibAssetToNode = useSeedanceWorkspaceStore((s) => s.applyLibAssetToNode);

  const showToast = useSeedanceUiStore((s) => s.showToast);
  const addDebug = useSeedanceUiStore((s) => s.addDebug);
  const setConfigVisible = useSeedanceUiStore((s) => s.setConfigVisible);
  const openConfig = useCallback(() => setConfigVisible(true), [setConfigVisible]);

  const libSelectedGroupId = useSeedanceLibraryStore((s) => s.libSelectedGroupId);
  const setLibSelectedGroupId = useSeedanceLibraryStore((s) => s.setLibSelectedGroupId);
  const libRemoteAssets = useSeedanceLibraryStore((s) => s.libRemoteAssets);
  const setLibRemoteAssets = useSeedanceLibraryStore((s) => s.setLibRemoteAssets);
  const setLibUploading = useSeedanceLibraryStore((s) => s.setLibUploading);
  const libPortraitHasMore = useSeedanceLibraryStore((s) => s.libPortraitHasMore);
  const libPortraitLoading = useSeedanceLibraryStore((s) => s.libPortraitLoading);
  const libPortraitLoadingMore = useSeedanceLibraryStore((s) => s.libPortraitLoadingMore);
  const setLibPortraitHasMore = useSeedanceLibraryStore((s) => s.setLibPortraitHasMore);
  const setLibPortraitLoading = useSeedanceLibraryStore((s) => s.setLibPortraitLoading);
  const setLibPortraitLoadingMore = useSeedanceLibraryStore((s) => s.setLibPortraitLoadingMore);
  const libPortraitSearchName = useSeedanceLibraryStore((s) => s.libPortraitSearchName);
  const libDeletingAssetId = useSeedanceLibraryStore((s) => s.libDeletingAssetId);
  const setLibDeletingAssetId = useSeedanceLibraryStore((s) => s.setLibDeletingAssetId);

  // Drawer-specific state
  const libDrawerRemoteAssets = useSeedanceLibraryStore((s) => s.libDrawerRemoteAssets);
  const setLibDrawerRemoteAssets = useSeedanceLibraryStore((s) => s.setLibDrawerRemoteAssets);
  const libDrawerPortraitHasMore = useSeedanceLibraryStore((s) => s.libDrawerPortraitHasMore);
  const setLibDrawerPortraitHasMore = useSeedanceLibraryStore((s) => s.setLibDrawerPortraitHasMore);
  const libDrawerPortraitLoading = useSeedanceLibraryStore((s) => s.libDrawerPortraitLoading);
  const setLibDrawerPortraitLoading = useSeedanceLibraryStore((s) => s.setLibDrawerPortraitLoading);
  const libDrawerPortraitLoadingMore = useSeedanceLibraryStore((s) => s.libDrawerPortraitLoadingMore);
  const setLibDrawerPortraitLoadingMore = useSeedanceLibraryStore((s) => s.setLibDrawerPortraitLoadingMore);
  const libDrawerSearchName = useSeedanceLibraryStore((s) => s.libDrawerSearchName);

  // Picker-specific state
  const libPickerRemoteAssets = useSeedanceLibraryStore((s) => s.libPickerRemoteAssets);
  const setLibPickerRemoteAssets = useSeedanceLibraryStore((s) => s.setLibPickerRemoteAssets);
  const libPickerPortraitHasMore = useSeedanceLibraryStore((s) => s.libPickerPortraitHasMore);
  const setLibPickerPortraitHasMore = useSeedanceLibraryStore((s) => s.setLibPickerPortraitHasMore);
  const libPickerPortraitLoading = useSeedanceLibraryStore((s) => s.libPickerPortraitLoading);
  const setLibPickerPortraitLoading = useSeedanceLibraryStore((s) => s.setLibPickerPortraitLoading);
  const libPickerPortraitLoadingMore = useSeedanceLibraryStore((s) => s.libPickerPortraitLoadingMore);
  const setLibPickerPortraitLoadingMore = useSeedanceLibraryStore((s) => s.setLibPickerPortraitLoadingMore);
  const libPickerSearchName = useSeedanceLibraryStore((s) => s.libPickerSearchName);

  // Route to layout-specific state
  const isDrawer = pagingLayout === 'drawer';
  const isPicker = pagingLayout === 'picker';
  const session = isDrawer ? drawerPagingSession : isPicker ? pickerPagingSession : libPortraitPagingSession;
  const effectiveRemoteAssets = isDrawer ? libDrawerRemoteAssets : isPicker ? libPickerRemoteAssets : libRemoteAssets;
  const effectiveSetRemoteAssets = isDrawer ? setLibDrawerRemoteAssets : isPicker ? setLibPickerRemoteAssets : setLibRemoteAssets;
  const effectiveHasMore = isDrawer ? libDrawerPortraitHasMore : isPicker ? libPickerPortraitHasMore : libPortraitHasMore;
  const effectiveSetHasMore = isDrawer ? setLibDrawerPortraitHasMore : isPicker ? setLibPickerPortraitHasMore : setLibPortraitHasMore;
  const effectiveLoading = isDrawer ? libDrawerPortraitLoading : isPicker ? libPickerPortraitLoading : libPortraitLoading;
  const effectiveSetLoading = isDrawer ? setLibDrawerPortraitLoading : isPicker ? setLibPickerPortraitLoading : setLibPortraitLoading;
  const effectiveLoadingMore = isDrawer ? libDrawerPortraitLoadingMore : isPicker ? libPickerPortraitLoadingMore : libPortraitLoadingMore;
  const effectiveSetLoadingMore = isDrawer ? setLibDrawerPortraitLoadingMore : isPicker ? setLibPickerPortraitLoadingMore : setLibPortraitLoadingMore;
  const effectiveSearchName = isDrawer ? libDrawerSearchName : isPicker ? libPickerSearchName : libPortraitSearchName;

  const libraryAssetAwaitingDelete = useSeedanceLibraryStore((s) => s.libraryAssetAwaitingDelete);
  const setLibraryAssetAwaitingDelete = useSeedanceLibraryStore((s) => s.setLibraryAssetAwaitingDelete);
  const libraryGroupAwaitingDelete = useSeedanceLibraryStore((s) => s.libraryGroupAwaitingDelete);
  const setLibraryGroupAwaitingDelete = useSeedanceLibraryStore((s) => s.setLibraryGroupAwaitingDelete);
  const setLibraryGroupDeleting = useSeedanceLibraryStore((s) => s.setLibraryGroupDeleting);
  const setLibPickerTarget = useSeedanceLibraryStore((s) => s.setLibPickerTarget);
  const clearLibPicker = useSeedanceLibraryStore((s) => s.clearLibPicker);

  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const drawerUploadRef = useRef<HTMLInputElement | null>(null);
  const libScrollRootRef = useRef<HTMLDivElement | null>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const scrollRootRef = scrollRootRefProp ?? libScrollRootRef;
  const loadMoreSentinelRefResolved = loadMoreSentinelRefProp ?? loadMoreSentinelRef;
  const sparseBackfillInFlightRef = useRef(false);

  const resolveAssetForWorkspace = useCallback((asset: Asset): Asset => {
    const resolved = resolveAssetForWorkspaceFromCache(
      asset,
      useSeedanceWorkspaceStore.getState().nodes,
    );
    void persistLibraryAssetPreviewCache([resolved]);
    return resolved;
  }, []);

  const readEffectiveRemoteAssets = useCallback(() => {
    const s = useSeedanceLibraryStore.getState();
    return isDrawer ? s.libDrawerRemoteAssets : isPicker ? s.libPickerRemoteAssets : s.libRemoteAssets;
  }, [isDrawer, isPicker]);

  const measureLibraryPageSize = useCallback(
    (mode: 'initial' | 'more') => {
      if (pagingLayout === 'drawer') {
        if (mode === 'initial') {
          const root = scrollRootRef.current;
          const headerEl = root?.closest('.lib-drawer')?.querySelector('.lib-drawer-header');
          const hintEl = root?.querySelector('.lib-drawer-hint');
          const headerH = headerEl?.getBoundingClientRect().height ?? 56;
          const hintH = hintEl?.getBoundingClientRect().height ?? 40;
          const pad =
            typeof window !== 'undefined' && root
              ? parseFloat(getComputedStyle(root).paddingTop || '0') +
              parseFloat(getComputedStyle(root).paddingBottom || '0')
              : 24;
          return computeLibraryDrawerPortraitInitialPageSize({
            viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
            reservedTopPx: headerH + hintH + pad + 8,
          });
        }
        return computeLibraryDrawerPortraitLoadMorePageSize();
      }

      if (pagingLayout === 'picker') {
        const root = scrollRootRef.current;
        const containerWidth =
          root?.clientWidth ??
          (typeof window !== 'undefined' ? Math.min(568, window.innerWidth * 0.9 - 32) : 568);
        if (mode === 'initial') {
          const countEl = root?.querySelector('.lp-count');
          const countH = countEl?.getBoundingClientRect().height ?? 28;
          const measuredBody = (root?.clientHeight ?? 0) - countH;
          const bodyHeight =
            measuredBody > 120
              ? measuredBody
              : typeof window !== 'undefined'
                ? window.innerHeight * 0.7 - 52 - 28
                : 400;
          return computeLibraryPickerPortraitInitialPageSize({ containerWidth, bodyHeight });
        }
        return computeLibraryPickerPortraitLoadMorePageSize(containerWidth);
      }

      const root = scrollRootRef.current;
      const toolbar = root?.querySelector('.lib-toolbar');
      const toolbarH = toolbar?.getBoundingClientRect().height ?? 120;
      const pad =
        typeof window !== 'undefined'
          ? parseFloat(getComputedStyle(root ?? document.documentElement).paddingTop || '0') +
          parseFloat(getComputedStyle(root ?? document.documentElement).paddingBottom || '0')
          : 32;
      const containerWidth =
        root?.clientWidth ??
        (typeof window !== 'undefined' ? Math.min(window.innerWidth - 280, 1200) : 960);
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
      if (mode === 'initial') {
        return computeLibraryPortraitInitialPageSize({
          viewportHeight,
          containerWidth,
          reservedTopPx: toolbarH + pad + 24,
        });
      }
      return computeLibraryPortraitLoadMorePageSize(containerWidth);
    },
    [pagingLayout, scrollRootRef],
  );

  const portraitListLabel =
    pagingLayout === 'drawer'
      ? '素材库抽屉'
      : pagingLayout === 'picker'
        ? '素材选择器'
        : '素材库';

  const fetchLibraryPortraitPage = useCallback(
    async (
      groupId: string,
      page: number,
      pageSize: number,
      seq: number,
      mode: 'replace' | 'append',
      signal?: AbortSignal,
    ) => {
      const state = useSeedanceLibraryStore.getState();
      const nameQuery = (
        isDrawer ? state.libDrawerSearchName : isPicker ? state.libPickerSearchName : state.libPortraitSearchName
      ).trim();
      const data: unknown = await listVirtualPortraits(
        {
          onDebug: (msg: string, isError?: boolean) => addDebug(msg, isError),
          signal,
        },
        groupId,
        {
          page_number: page,
          page_size: pageSize,
          ...(nameQuery ? { name: nameQuery } : {}),
        },
      );
      if (seq !== session.fetchSeq) return null;
      let assets = mapVirtualPortraitApiListToAssets(data);
      const rawPageCount = assets.length;
      if (nameQuery) {
        assets = assets.filter((a) => assetNameMatchesSearch(a.name, nameQuery));
      }
      // 先合并同素材组的本地预览，再补齐真正缺失的 URL；否则每次进入素材库都会
      // 对已经缓存的素材重复发起一轮 GetAsset 详情请求。
      if (session.cachedGroupId === groupId && session.cachedAssets.length) {
        assets = fillMissingPreviews(assets, session.cachedAssets);
      }
      if (groupId.trim()) {
        assets = await enrichPortraitAssetsMissingUrls(
          assets,
          groupId,
          VIRTUAL_PORTRAIT_PROJECT,
          {
            onDebug: (msg: string, isError?: boolean) => addDebug(msg, isError),
            signal,
          },
        );
        if (seq !== session.fetchSeq) return null;
        await persistLibraryAssetPreviewCache(assets);
        if (seq !== session.fetchSeq) return null;
        backfillWorkspaceNodesFromPreviewSources(assets);
      }
      if (seq !== session.fetchSeq) return null;
      if (mode === 'replace') {
        effectiveSetRemoteAssets(assets);
        session.cachedGroupId = groupId;
        session.cachedAssets = mergeSessionPreviewAssets(session.cachedAssets, assets);
        if (!nameQuery && !isDrawer && !isPicker) {
          void persistLibraryGroupAssetsSnapshot(groupId, assets);
        } else {
          void persistLibraryAssetPreviewCache(assets);
        }
      } else {
        effectiveSetRemoteAssets((prev) => {
          const seen = new Set(prev.map((a) => a.id));
          const merged = [...prev, ...assets.filter((a) => !seen.has(a.id))];
          if (!nameQuery && !isDrawer && !isPicker) {
            void persistLibraryGroupAssetsSnapshot(groupId, merged);
          } else {
            void persistLibraryAssetPreviewCache(assets);
          }
          return merged;
        });
        session.cachedGroupId = groupId;
        session.cachedAssets = mergeSessionPreviewAssets(session.cachedAssets, assets);
      }
      session.page = page;
      if (mode === 'replace') {
        session.loadedGroupId = groupId;
        session.loadedSearchName = nameQuery;
      }

      const totalCount = (data as any)?.data?.TotalCount ?? (data as any)?.data?.Result?.TotalCount;
      if (typeof totalCount === 'number') {
        effectiveSetHasMore(page * pageSize < totalCount);
      } else {
        effectiveSetHasMore(rawPageCount >= pageSize);
      }

      return assets;
    },
    [addDebug, effectiveSetHasMore, effectiveSetRemoteAssets, isDrawer, isPicker, session],
  );

  const appendSparsePortraitPages = useCallback(
    async (
      groupId: string,
      seq: number,
      basePageSize: number,
      filter: LibraryMediaFilter,
      lastPageCount: number,
      lastPageSize: number,
      signal?: AbortSignal,
    ) => {
      let batchCount = lastPageCount;
      let batchPageSize = lastPageSize;
      let hasMore = batchCount >= batchPageSize;
      let backfillAttempt = 0;
      let loadedCount = readEffectiveRemoteAssets().length;

      while (
        hasMore &&
        backfillAttempt < LIBRARY_SPARSE_BACKFILL_MAX_PAGES &&
        shouldContinueSparseBackfill({
          filter,
          loadedCount,
          lastPageCount: batchCount,
          lastPageSize: batchPageSize,
          hasMore,
          minFillCount: basePageSize,
        })
      ) {
        if (seq !== session.fetchSeq) return;
        const nextPageSize = computeAdaptiveBackfillPageSize(basePageSize, backfillAttempt + 1);
        const nextPage = session.page + 1;
        backfillAttempt += 1;
        addDebug(
          `${portraitListLabel}：稀疏补拉 page=${nextPage} page_size=${nextPageSize}（合并加载，减少请求次数）`,
        );
        session.pageSize = nextPageSize;
        const nextBatch = await fetchLibraryPortraitPage(
          groupId,
          nextPage,
          nextPageSize,
          seq,
          'append',
          signal,
        );
        if (!nextBatch || seq !== session.fetchSeq) return;
        batchCount = nextBatch.length;
        batchPageSize = nextPageSize;
        hasMore = batchCount >= batchPageSize;
        loadedCount = readEffectiveRemoteAssets().length;
      }
    },
    [
      addDebug,
      fetchLibraryPortraitPage,
      portraitListLabel,
      readEffectiveRemoteAssets,
      session,
    ],
  );

  const runSparsePortraitBackfill = useCallback(
    async (groupId: string, seq: number, filter: LibraryMediaFilter, signal?: AbortSignal) => {
      if (seq !== session.fetchSeq) return;
      if (sparseBackfillInFlightRef.current) return;

      const store = useSeedanceLibraryStore.getState();
      const hasMore = isDrawer
        ? store.libDrawerPortraitHasMore
        : isPicker
          ? store.libPickerPortraitHasMore
          : store.libPortraitHasMore;
      if (!hasMore) return;

      const basePageSize = session.pageSize || measureLibraryPageSize('initial');
      const loaded = readEffectiveRemoteAssets();
      if (filter === 'all' && loaded.length >= basePageSize) return;

      const lastPageSize = session.pageSize || basePageSize;
      sparseBackfillInFlightRef.current = true;
      try {
        await appendSparsePortraitPages(
          groupId,
          seq,
          basePageSize,
          filter,
          lastPageSize,
          lastPageSize,
          signal,
        );
      } finally {
        sparseBackfillInFlightRef.current = false;
      }
    },
    [
      appendSparsePortraitPages,
      isDrawer,
      isPicker,
      measureLibraryPageSize,
      readEffectiveRemoteAssets,
      session,
    ],
  );

  const loadLibraryPortraitsInitial = useCallback(
    async (groupId: string, seq: number, signal?: AbortSignal) => {
      if (seq !== session.fetchSeq) return;
      const basePageSize = measureLibraryPageSize('initial');
      session.pageSize = basePageSize;
      session.page = 0;
      effectiveSetLoading(true);
      effectiveSetHasMore(false);
      try {
        session.cachedGroupId = '';
        session.cachedAssets = [];
        const [snapshot, previewCache] = await Promise.all([
          loadLibraryGroupAssetsSnapshot(),
          loadLibraryAssetPreviewCache(),
        ]);
        if (seq !== session.fetchSeq) return;
        const initialPreviewState = prepareLibraryInitialPreviewState(groupId, snapshot, previewCache);
        session.cachedGroupId = groupId;
        session.cachedAssets = initialPreviewState.previewAssets;
        if (initialPreviewState.snapshotAssets.length) {
          const snapshotAssets = initialPreviewState.snapshotAssets;
          const searchName = effectiveSearchName.trim();
          const cachedAssets = searchName
            ? snapshotAssets.filter((asset) => assetNameMatchesSearch(asset.name, searchName))
            : snapshotAssets;
          effectiveSetRemoteAssets(cachedAssets);
          effectiveSetHasMore(true);
          addDebug(`${portraitListLabel}：已从本地快照恢复 ${cachedAssets.length} 个素材，后台刷新列表`);
        } else {
          effectiveSetRemoteAssets([]);
        }
        addDebug(
          `${portraitListLabel}：首屏加载 page=1 page_size=${basePageSize}${effectiveSearchName.trim()
            ? ` name=${effectiveSearchName.trim()}`
            : ''
          }（按视口高度估算）`,
        );
        const firstBatch = await fetchLibraryPortraitPage(
          groupId,
          1,
          basePageSize,
          seq,
          'replace',
          signal,
        );
        if (!firstBatch || seq !== session.fetchSeq) return;
        await appendSparsePortraitPages(
          groupId,
          seq,
          basePageSize,
          portraitMediaFilter,
          firstBatch.length,
          basePageSize,
          signal,
        );
      } catch (e: unknown) {
        if (isAbortLike(e)) return;
        const msg = e instanceof Error ? e.message : String(e);
        addDebug(`获取素材列表失败: ${msg}`, true);
        showToast('获取素材列表失败: ' + msg, 'e');
      } finally {
        if (seq === session.fetchSeq) effectiveSetLoading(false);
      }
    },
    [
      addDebug,
      appendSparsePortraitPages,
      effectiveSearchName,
      effectiveSetHasMore,
      effectiveSetLoading,
      effectiveSetRemoteAssets,
      fetchLibraryPortraitPage,
      measureLibraryPageSize,
      portraitListLabel,
      portraitMediaFilter,
      session,
      showToast,
    ],
  );

  const loadLibraryPortraitsInitialRef = useRef(loadLibraryPortraitsInitial);
  loadLibraryPortraitsInitialRef.current = loadLibraryPortraitsInitial;

  const loadMoreLibraryPortraits = useCallback(
    async (groupId: string, seq: number) => {
      if (seq !== session.fetchSeq) return;
      if (sparseBackfillInFlightRef.current) return;
      if (effectiveLoading || effectiveLoadingMore) return;
      if (!effectiveHasMore) return;
      const pageSize = measureLibraryPageSize('more');
      const nextPage = session.page + 1;
      effectiveSetLoadingMore(true);
      try {
        addDebug(`${portraitListLabel}：下拉加载 page=${nextPage} page_size=${pageSize}`);
        await fetchLibraryPortraitPage(groupId, nextPage, pageSize, seq, 'append');
      } catch (e: unknown) {
        if (isAbortLike(e)) return;
        const msg = e instanceof Error ? e.message : String(e);
        addDebug(`加载更多素材失败: ${msg}`, true);
        showToast('加载更多失败: ' + msg, 'e');
      } finally {
        effectiveSetLoadingMore(false);
      }
    },
    [
      addDebug,
      effectiveHasMore,
      effectiveLoading,
      effectiveLoadingMore,
      effectiveSetLoadingMore,
      fetchLibraryPortraitPage,
      measureLibraryPageSize,
      portraitListLabel,
      session,
      showToast,
    ],
  );

  const createLibraryGroup = useCallback(async () => {
    const name = useSeedanceLibraryStore.getState().newGroupName.trim();
    const desc = useSeedanceLibraryStore.getState().newGroupDescription.trim();
    if (!name) {
      showToast('素材库名称不能为空', 'e');
      return;
    }
    if (!hasTaskListConfig()) {
      showToast('请先配置系统访问令牌和用户 ID', 'e');
      openConfig();
      return;
    }
    try {
      await createVirtualPortraitGroup(
        { project_name: VIRTUAL_PORTRAIT_PROJECT, name, description: desc },
        { onDebug: (msg: string, isError?: boolean) => addDebug(msg, isError) },
      );
      showToast('素材组创建成功', 's');
      useSeedanceLibraryStore.getState().setCreateGroupVisible(false);
      useSeedanceLibraryStore.getState().setNewGroupName('');
      useSeedanceLibraryStore.getState().setNewGroupDescription('');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      addDebug(`创建素材组失败: ${msg}`, true);
      showToast('创建失败: ' + msg, 'e');
    }
  }, [addDebug, hasTaskListConfig, openConfig, showToast]);

  const requestLibraryGroupDelete = useCallback(
    (group: LibraryGroup) => {
      setLibraryGroupAwaitingDelete(group);
    },
    [setLibraryGroupAwaitingDelete],
  );

  const dismissLibraryGroupDeleteModal = useCallback(() => {
    setLibraryGroupAwaitingDelete(null);
  }, [setLibraryGroupAwaitingDelete]);

  const commitLibraryGroupDelete = useCallback(async () => {
    const group = libraryGroupAwaitingDelete;
    if (!group) return;
    if (!hasTaskListConfig()) {
      showToast('请先配置系统访问令牌和用户 ID', 'e');
      openConfig();
      return;
    }
    setLibraryGroupDeleting(true);
    try {
      await deleteVirtualPortraitGroup(group.id, VIRTUAL_PORTRAIT_PROJECT, {
        onDebug: (msg: string, isError?: boolean) => addDebug(msg, isError),
      });
      showToast('已删除素材组', 's');
      setLibraryGroupAwaitingDelete(null);
      if (libSelectedGroupId === group.id) {
        setLibSelectedGroupId('');
        setLibRemoteAssets([]);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      addDebug(`删除素材组失败: ${msg}`, true);
      showToast('删除失败: ' + msg, 'e');
    } finally {
      setLibraryGroupDeleting(false);
    }
  }, [
    addDebug,
    hasTaskListConfig,
    libraryGroupAwaitingDelete,
    libSelectedGroupId,
    openConfig,
    setLibRemoteAssets,
    setLibSelectedGroupId,
    setLibraryGroupAwaitingDelete,
    setLibraryGroupDeleting,
    showToast,
  ]);

  const uploadPortraitsToSelectedGroup = useCallback(
    async (input: HTMLInputElement | { files: FileList }) => {
      const gid = libSelectedGroupId;
      const allFiles = Array.from(input.files || []);
      if ('value' in input) input.value = '';

      if (!allFiles.length) return;
      if (!gid) {
        showToast('请先在上方输入并保存素材组 ID', 'e');
        return;
      }
      if (!hasTaskListConfig()) {
        showToast('请先配置系统访问令牌和用户 ID', 'e');
        openConfig();
        return;
      }

      const imageFiles = allFiles.filter((f) => f.type.startsWith('image/'));
      const videoFiles = allFiles.filter((f) => f.type.startsWith('video/'));
      const audioCandidates = allFiles.filter(isLibraryAudioFile);

      // 视频大小限制：50MB
      const MAX_VIDEO_SIZE = 50 * 1024 * 1024;
      const validVideoFiles = videoFiles.filter((f) => f.size <= MAX_VIDEO_SIZE);
      const oversizedVideos = videoFiles.filter((f) => f.size > MAX_VIDEO_SIZE);

      if (oversizedVideos.length > 0) {
        showToast(
          `以下视频超过了 50MB 限制，已跳过：${oversizedVideos.map((v) => v.name).join(', ')}`,
          'i',
        );
      }

      const audioValidation = await validateLibraryAudioFiles(audioCandidates);
      if (audioValidation.invalidFormat.length > 0) {
        showToast(
          `仅支持 mp3、wav 格式，已跳过：${audioValidation.invalidFormat.map((f) => f.name).join(', ')}`,
          'i',
        );
      }
      if (audioValidation.oversized.length > 0) {
        showToast(
          `以下音频超过了 15MB 限制，已跳过：${audioValidation.oversized.map((f) => f.name).join(', ')}`,
          'i',
        );
      }
      if (audioValidation.invalidDuration.length > 0) {
        showToast(
          `以下音频时长不在 2–15 秒范围内，已跳过：${audioValidation.invalidDuration.map(({ file }) => file.name).join(', ')}`,
          'i',
        );
      }

      const validAudioFiles = audioValidation.valid;
      const totalToUpload = imageFiles.length + validVideoFiles.length + validAudioFiles.length;
      if (totalToUpload === 0) return;

      const gname = gid;
      setLibUploading(true);
      try {
        // 1. 上传图片
        if (imageFiles.length > 0) {
          const imageUrls = await batchUploadImagesToOss(imageFiles, {
            onDebug: (msg: string, isError?: boolean) => addDebug(msg, isError),
          });
          for (let i = 0; i < imageFiles.length; i++) {
            const file = imageFiles[i];
            const url = imageUrls[i];
            const name = file.name.replace(/\.[^.]+$/, '') || '未命名';
            await createVirtualPortrait(
              {
                group_id: gid,
                url,
                name,
                asset_type: VP_ASSET_TYPE_IMAGE,
              },
              { onDebug: (msg: string, isError?: boolean) => addDebug(msg, isError) },
            );
          }
        }

        // 2. 上传视频
        if (validVideoFiles.length > 0) {
          const videoUrls = await batchUploadVideosToOss(validVideoFiles, {
            onDebug: (msg: string, isError?: boolean) => addDebug(msg, isError),
          });
          for (let i = 0; i < validVideoFiles.length; i++) {
            const file = validVideoFiles[i];
            const url = videoUrls[i];
            const name = file.name.replace(/\.[^.]+$/, '') || '未命名';
            await createVirtualPortrait(
              {
                group_id: gid,
                url,
                name,
                asset_type: VP_ASSET_TYPE_VIDEO,
              },
              { onDebug: (msg: string, isError?: boolean) => addDebug(msg, isError) },
            );
          }
        }

        // 3. 上传音频
        if (validAudioFiles.length > 0) {
          const audioUrls = await batchUploadAudiosToOss(validAudioFiles, {
            onDebug: (msg: string, isError?: boolean) => addDebug(msg, isError),
          });
          for (let i = 0; i < validAudioFiles.length; i++) {
            const file = validAudioFiles[i];
            const url = audioUrls[i];
            const name = file.name.replace(/\.[^.]+$/, '') || '未命名';
            await createVirtualPortrait(
              {
                group_id: gid,
                url,
                name,
                asset_type: VP_ASSET_TYPE_AUDIO,
              },
              { onDebug: (msg: string, isError?: boolean) => addDebug(msg, isError) },
            );
          }
        }

        showToast(`已上传 ${totalToUpload} 个素材到「${gname}」`, 's');
        const { seq, signal } = startPortraitListFetch(session);
        await loadLibraryPortraitsInitial(gid, seq, signal);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        addDebug(`上传素材失败: ${msg}`, true);
        showToast('上传失败: ' + msg, 'e');
      } finally {
        setLibUploading(false);
      }
    },
    [
      addDebug,
      hasTaskListConfig,
      libSelectedGroupId,
      loadLibraryPortraitsInitial,
      openConfig,
      setLibUploading,
      showToast,
    ],
  );

  const requestLibraryAssetDelete = useCallback(
    (asset: Asset) => {
      setLibraryAssetAwaitingDelete(asset);
    },
    [setLibraryAssetAwaitingDelete],
  );

  const dismissLibraryAssetDeleteModal = useCallback(() => {
    if (libraryAssetAwaitingDelete && libDeletingAssetId === libraryAssetAwaitingDelete.id) {
      return;
    }
    setLibraryAssetAwaitingDelete(null);
  }, [libDeletingAssetId, libraryAssetAwaitingDelete, setLibraryAssetAwaitingDelete]);

  const commitLibraryAssetDelete = useCallback(async () => {
    const asset = libraryAssetAwaitingDelete;
    if (!asset) return;
    const assetId = asset.sourceAssetId?.trim();
    if (!assetId) {
      setLibraryAssetAwaitingDelete(null);
      return;
    }
    setLibDeletingAssetId(asset.id);
    try {
      await deleteVirtualPortrait(assetId, {
        onDebug: (msg: string, isError?: boolean) => addDebug(msg, isError),
      });
      showToast('已删除素材', 's');
      setLibraryAssetAwaitingDelete(null);
      setNodes((prev) =>
        prev.map((n) =>
          n.sourceAssetId === assetId ? { ...n, sourceAssetId: undefined, connected: false } : n,
        ),
      );
      void persistSeedanceWorkspaceState();
      removeLibraryAssetFromSharedStores(asset);
      if (libSelectedGroupId) {
        const { seq, signal } = startPortraitListFetch(session);
        await loadLibraryPortraitsInitial(libSelectedGroupId, seq, signal);
      }
      else effectiveSetRemoteAssets((prev) => prev.filter((x) => x.id !== asset.id));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      addDebug(`删除素材失败: ${msg}`, true);
      showToast('删除失败: ' + msg, 'e');
    } finally {
      setLibDeletingAssetId(null);
    }
  }, [
    addDebug,
    effectiveSetRemoteAssets,
    libraryAssetAwaitingDelete,
    libSelectedGroupId,
    loadLibraryPortraitsInitial,
    session,
    setLibDeletingAssetId,
    setLibraryAssetAwaitingDelete,
    setNodes,
    showToast,
  ]);

  const applyLibAsset = (nodeId: string, assetId: string) => {
    const asset = effectiveRemoteAssets.find((a) => a.id === assetId);
    if (!asset) return;
    const resolvedAsset = resolveAssetForWorkspace(asset);
    applyLibAssetToNode(nodeId, resolvedAsset);
    void persistSeedanceWorkspaceState();
    clearLibPicker();
    showToast(`已应用素材: ${resolvedAsset.name}`, 's');
  };

  const pickAssetFromDrawer = (assetId: string) => {
    const asset = effectiveRemoteAssets.find((a) => a.id === assetId);
    if (!asset) return;
    const resolvedAsset = resolveAssetForWorkspace(asset);
    addMatNode(resolvedAsset.type, {
      name: resolvedAsset.name,
      url: resolvedAsset.url || '',
      thumb: resolvedAsset.thumb || '',
      sourceAssetId: resolvedAsset.sourceAssetId || resolvedAsset.id,
    });
    void persistSeedanceWorkspaceState();
    showToast('已添加到工作台', 's');
  };

  const addMatFromLib = (id: string) => {
    const asset = effectiveRemoteAssets.find((x) => x.id === id);
    if (!asset) {
      showToast('找不到素材信息', 'e');
      return;
    }
    const resolvedAsset = resolveAssetForWorkspace(asset);
    // 1. 创建对应类型的节点
    const newNode = addMatNode(resolvedAsset.type);
    // 2. 将素材应用到该节点
    applyLibAssetToNode(newNode.id, resolvedAsset);

    void persistSeedanceWorkspaceState();
    showToast(`已添加${resolvedAsset.type === 'video' ? '视频' : resolvedAsset.type === 'audio' ? '音频' : '图片'}到工作台`, 's');
  };

  const openLibPicker = (nodeId: string) => setLibPickerTarget(nodeId);

  const refreshLibraryPortraits = useCallback(
    (overrideGroupId?: string) => {
      const gid = (overrideGroupId ?? libSelectedGroupId).trim();
      if (!gid) return;
      session.loadedGroupId = '';
      session.loadedSearchName = '';
      const { seq, signal } = startPortraitListFetch(session);
      void loadLibraryPortraitsInitial(gid, seq, signal);
    },
    [libSelectedGroupId, loadLibraryPortraitsInitial, session],
  );

  useEffect(() => {
    if (!active) return;
    if (!hasTaskListConfig()) {
      setConfigVisible(true);
    }
  }, [active, hasTaskListConfig, setConfigVisible]);

  /** 抽屉/选择器：搜索词变化时自动重载；主页面由搜索按钮显式触发 refreshLibraryPortraits */
  const layoutSearchName = isDrawer ? libDrawerSearchName : isPicker ? libPickerSearchName : null;

  useEffect(() => {
    if (!portraitListManaged) return;
    if (!active) {
      abortPortraitListSession(session);
      session.fetchSeq += 1;
      effectiveSetLoading(false);
      effectiveSetLoadingMore(false);
      return;
    }
    if (!portraitListEnabled) {
      return;
    }
    if (!libSelectedGroupId) {
      abortPortraitListSession(session);
      session.fetchSeq += 1;
      session.loadedGroupId = '';
      session.loadedSearchName = '';
      effectiveSetRemoteAssets([]);
      effectiveSetHasMore(false);
      effectiveSetLoading(false);
      return;
    }
    if (pagingLayout === 'page' && session.loadedGroupId !== libSelectedGroupId) {
      session.loadedGroupId = '';
      session.loadedSearchName = '';
    }
    const appliedSearchName = (
      layoutSearchName ?? useSeedanceLibraryStore.getState().libPortraitSearchName
    ).trim();
    if (skipInitialLoadIfPopulated) {
      const curLength = (() => {
        const s = useSeedanceLibraryStore.getState();
        return isDrawer
          ? s.libDrawerRemoteAssets.length
          : isPicker
            ? s.libPickerRemoteAssets.length
            : s.libRemoteAssets.length;
      })();
      if (
        curLength > 0 &&
        session.loadedGroupId === libSelectedGroupId &&
        session.loadedSearchName === appliedSearchName
      ) {
        return;
      }
    }
    const { seq, signal } = startPortraitListFetch(session);
    void loadLibraryPortraitsInitialRef.current(libSelectedGroupId, seq, signal);
  }, [
    active,
    effectiveSetHasMore,
    effectiveSetRemoteAssets,
    isDrawer,
    isPicker,
    layoutSearchName,
    libSelectedGroupId,
    pagingLayout,
    portraitListEnabled,
    portraitListManaged,
    session,
    skipInitialLoadIfPopulated,
  ]);

  useEffect(() => {
    if (!portraitListManaged || pagingLayout !== 'page') return;
    if (!active || !libSelectedGroupId || effectiveLoading) return;
    if (portraitMediaFilter === 'all') return;
    void runSparsePortraitBackfill(libSelectedGroupId, session.fetchSeq, portraitMediaFilter);
  }, [
    active,
    effectiveLoading,
    libSelectedGroupId,
    pagingLayout,
    portraitListManaged,
    portraitMediaFilter,
    runSparsePortraitBackfill,
    session,
  ]);

  useEffect(() => {
    if (!portraitListManaged) return;
    if (!active || !libSelectedGroupId || !effectiveHasMore) return;
    const root = scrollRootRef.current;
    const sentinel = loadMoreSentinelRefResolved.current;
    if (!root || !sentinel) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          void loadMoreLibraryPortraits(libSelectedGroupId, session.fetchSeq);
        }
      },
      { root, rootMargin: pagingLayout === 'page' ? '240px 0px' : '120px 0px', threshold: 0 },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [
    active,
    effectiveHasMore,
    effectiveLoading,
    effectiveLoadingMore,
    effectiveRemoteAssets.length,
    libSelectedGroupId,
    loadMoreLibraryPortraits,
    loadMoreSentinelRefResolved,
    pagingLayout,
    portraitListManaged,
    scrollRootRef,
    session,
  ]);

  useEffect(() => {
    if (libSelectedGroupId) {
      setLocalStorageValue(SEEDANCE_STORAGE_KEYS.librarySelectedGroupId, libSelectedGroupId);
    } else {
      removeLocalStorageValue(SEEDANCE_STORAGE_KEYS.librarySelectedGroupId);
    }
  }, [libSelectedGroupId]);

  return {
    createLibraryGroup,
    dismissLibraryAssetDeleteModal,
    commitLibraryAssetDelete,
    dismissLibraryGroupDeleteModal,
    commitLibraryGroupDelete,
    uploadPortraitsToSelectedGroup,
    pickAssetFromDrawer,
    requestLibraryAssetDelete,
    applyLibAsset,
    openLibPicker,
    openConfig,
    requestLibraryGroupDelete,
    addMatFromLib,
    uploadInputRef,
    drawerUploadRef,
    libScrollRootRef,
    loadMoreSentinelRef,
    libPortraitHasMore: effectiveHasMore,
    libPortraitLoading: effectiveLoading,
    libPortraitLoadingMore: effectiveLoadingMore,
    reloadLibraryPortraitsInitial: loadLibraryPortraitsInitial,
    refreshLibraryPortraits,
  };
};

const ICONS: Record<MediaType, string> = { image: '🖼️', video: '🎬', audio: '🎵' };

export type LibraryPortraitKind = 'real' | 'virtual';

export type { LibraryMediaFilter } from '../../utils/libraryPortraitSparseLoad';

const LIBRARY_PORTRAIT_KIND_LABEL: Record<LibraryPortraitKind, string> = {
  real: '真人人像资产',
  virtual: '虚拟人像资产',
};

const LIBRARY_MEDIA_FILTER_LABEL: Record<LibraryMediaFilter, string> = {
  all: '全部',
  image: '图片',
  video: '视频',
  audio: '音频',
};

const libraryMediaFilterCountLabel = (filter: LibraryMediaFilter): string =>
  filter === 'all' ? '素材' : LIBRARY_MEDIA_FILTER_LABEL[filter];

const LibraryLoadingSpinner: React.FC = () => (
  <span className="lib-loading-spinner" aria-hidden />
);

const LibraryBootstrapPanel: React.FC<{ label: string }> = ({ label }) => (
  <div className="lib-bootstrap-loading" role="status" aria-live="polite" aria-label={label}>
    <LibraryLoadingSpinner />
    <span>{label}</span>
  </div>
);

const LibraryPage: React.FC = () => {
  const hasTaskListConfig = useCallback(() => {
    const userId = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.configUserId).trim();
    return Boolean(userId);
  }, []);

  const libSelectedGroupId = useSeedanceLibraryStore((s) => s.libSelectedGroupId);
  const setLibSelectedGroupId = useSeedanceLibraryStore((s) => s.setLibSelectedGroupId);
  const libUploading = useSeedanceLibraryStore((s) => s.libUploading);
  const libDisplayAssets = useSeedanceLibraryStore((s) => s.libRemoteAssets);
  const libDeletingAssetId = useSeedanceLibraryStore((s) => s.libDeletingAssetId);
  const libPortraitSearchName = useSeedanceLibraryStore((s) => s.libPortraitSearchName);
  const setLibPortraitSearchName = useSeedanceLibraryStore((s) => s.setLibPortraitSearchName);

  const [portraitKind, setPortraitKind] = useState<LibraryPortraitKind>('virtual');
  const [virtualGroupOptions, setVirtualGroupOptions] = useState<VirtualAvatarGroupOption[]>([]);
  const [virtualGroupsLoading, setVirtualGroupsLoading] = useState(false);
  /** group 接口完成并选好素材组后再拉 virtual_portrait 列表 */
  const [virtualGroupsReady, setVirtualGroupsReady] = useState(false);
  const [virtualMediaFilter, setVirtualMediaFilter] = useState<LibraryMediaFilter>('all');
  const [realGroupId, setRealGroupId] = useState(() =>
    getLocalStorageValue(SEEDANCE_STORAGE_KEYS.realPortraitGroupId).trim(),
  );
  const [realAuthBusy, setRealAuthBusy] = useState(false);
  const [realAuthStatus, setRealAuthStatus] = useState('');
  const [realGroupBindBusy, setRealGroupBindBusy] = useState(false);
  const realAuthPollAbortRef = useRef<AbortController | null>(null);
  const realLoadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const virtualGroupsBootstrappedRef = useRef(false);
  const virtualGroupsInflightRef = useRef<Promise<string> | null>(null);
  const [nameSearchInput, setNameSearchInput] = useState(libPortraitSearchName);
  const showToast = useSeedanceUiStore((s) => s.showToast);
  const addDebug = useSeedanceUiStore((s) => s.addDebug);
  const setModalVideoUrl = useSeedanceUiStore((s) => s.setModalVideoUrl);
  const setModalImageSrc = useSeedanceUiStore((s) => s.setModalImageSrc);
  const setModalAudioUrl = useSeedanceUiStore((s) => s.setModalAudioUrl);
  const setModalAudioTitle = useSeedanceUiStore((s) => s.setModalAudioTitle);

  const {
    openConfig,
    uploadInputRef,
    uploadPortraitsToSelectedGroup,
    requestLibraryAssetDelete,
    addMatFromLib,
    libScrollRootRef,
    loadMoreSentinelRef,
    libPortraitHasMore,
    libPortraitLoading,
    libPortraitLoadingMore,
    refreshLibraryPortraits,
  } = useLibraryPageController({
    active: portraitKind === 'virtual',
    portraitListEnabled: virtualGroupsReady,
    hasTaskListConfig,
    portraitMediaFilter: virtualMediaFilter,
  });

  const realLib = useRealPersonLibrary({
    active: portraitKind === 'real',
    hasTaskListConfig,
    openConfig,
    showToast,
    addDebug,
    scrollRootRef: libScrollRootRef,
    loadMoreSentinelRef: realLoadMoreSentinelRef,
  });

  const {
    boundGroupIds: realBoundGroupIds,
    groupsLoading: realGroupsLoading,
    selectedGroupId: realSelectedGroupId,
    assets: realDisplayAssets,
    loading: realPortraitLoading,
    loadingMore: realPortraitLoadingMore,
    hasMore: realPortraitHasMore,
    uploading: realUploading,
    deletingAssetId: realDeletingAssetId,
    uploadInputRef: realUploadInputRef,
    refreshBoundGroups: refreshRealBoundGroups,
    reloadAssets: reloadRealAssets,
    handleRealGroupChange,
    uploadToSelectedGroup: uploadRealPortraitsToSelectedGroup,
    deleteAsset: deleteRealAsset,
  } = realLib;

  const virtualPersonApiOptions = useCallback(
    () => ({ onDebug: (msg: string, isError?: boolean) => addDebug(msg, isError) }),
    [addDebug],
  );

  const loadVirtualAvatarGroups = useCallback(
    async (opts?: { keepCurrentSelection?: boolean }): Promise<string> => {
      if (virtualGroupsInflightRef.current) {
        return virtualGroupsInflightRef.current;
      }
      const task = (async (): Promise<string> => {
        if (!hasTaskListConfig()) {
          setVirtualGroupOptions([]);
          setVirtualGroupsReady(false);
          return '';
        }
        setVirtualGroupsLoading(true);
        if (!opts?.keepCurrentSelection) {
          setVirtualGroupsReady(false);
        }
        try {
          const customGroupId = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.customGroupId).trim();
          let info;
          if (customGroupId) {
            info = {
              groupId: customGroupId,
            };
          } else {
            info = await getUserAvatarAssetGroup(virtualPersonApiOptions());
          }
          const options = buildVirtualAvatarGroupOptions(info as any);
          setVirtualGroupOptions(options);
          const current = useSeedanceLibraryStore.getState().libSelectedGroupId;
          const nextId = opts?.keepCurrentSelection
            ? resolveVirtualAvatarGroupSelection(options, current)
            : pickInitialVirtualAvatarGroupId(options);
          if (nextId !== current) {
            setLibSelectedGroupId(nextId);
          } else if (!nextId && current) {
            setLibSelectedGroupId('');
          }
          setVirtualGroupsReady(Boolean(nextId));
          return nextId;
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : String(e);
          addDebug(`获取素材组失败: ${msg}`, true);
          showToast('获取素材组失败: ' + msg, 'e');
          setVirtualGroupOptions([]);
          setVirtualGroupsReady(false);
          return '';
        } finally {
          setVirtualGroupsLoading(false);
        }
      })();
      virtualGroupsInflightRef.current = task;
      try {
        return await task;
      } finally {
        if (virtualGroupsInflightRef.current === task) {
          virtualGroupsInflightRef.current = null;
        }
      }
    },
    [addDebug, hasTaskListConfig, setLibSelectedGroupId, showToast, virtualPersonApiOptions],
  );

  const loadVirtualAvatarGroupsRef = useRef(loadVirtualAvatarGroups);
  loadVirtualAvatarGroupsRef.current = loadVirtualAvatarGroups;

  useEffect(() => {
    if (portraitKind !== 'virtual') {
      virtualGroupsBootstrappedRef.current = false;
      setVirtualGroupsReady(false);
      return;
    }
    if (virtualGroupsBootstrappedRef.current) return;
    virtualGroupsBootstrappedRef.current = true;
    void loadVirtualAvatarGroupsRef.current({ keepCurrentSelection: false });
  }, [portraitKind]);

  // 只在外部清空时（如切换素材组）同步输入框
  useEffect(() => {
    if (!libPortraitSearchName) setNameSearchInput('');
  }, [libPortraitSearchName]);

  const submitNameSearch = useCallback(() => {
    setLibPortraitSearchName(nameSearchInput.trim());
    refreshLibraryPortraits();
  }, [nameSearchInput, refreshLibraryPortraits, setLibPortraitSearchName]);

  const clearNameSearch = useCallback(() => {
    setNameSearchInput('');
    setLibPortraitSearchName('');
    refreshLibraryPortraits();
  }, [refreshLibraryPortraits, setLibPortraitSearchName]);

  const refreshAssets = useCallback(async () => {
    const prevGroupId = useSeedanceLibraryStore.getState().libSelectedGroupId.trim();
    const gid = await loadVirtualAvatarGroups({ keepCurrentSelection: true });
    if (!gid) return;
    if (gid !== prevGroupId) return;
    refreshLibraryPortraits(gid);
  }, [loadVirtualAvatarGroups, refreshLibraryPortraits]);

  const handleVirtualGroupChange = useCallback(
    (nextGroupId: string) => {
      setLibSelectedGroupId(nextGroupId.trim());
    },
    [setLibSelectedGroupId],
  );

  const hasConfig = hasTaskListConfig();

  const virtualBootstrapLoading = useMemo(
    () =>
      portraitKind === 'virtual' &&
      hasConfig &&
      ((virtualGroupsLoading && libDisplayAssets.length === 0) ||
        (Boolean(libSelectedGroupId) && libPortraitLoading && libDisplayAssets.length === 0)),
    [
      portraitKind,
      hasConfig,
      virtualGroupsLoading,
      libSelectedGroupId,
      libPortraitLoading,
      libDisplayAssets.length,
    ],
  );

  const realBootstrapLoading = useMemo(
    () =>
      portraitKind === 'real' &&
      hasConfig &&
      (realGroupsLoading ||
        (Boolean(realSelectedGroupId) && realPortraitLoading && realDisplayAssets.length === 0)),
    [
      portraitKind,
      hasConfig,
      realGroupsLoading,
      realSelectedGroupId,
      realPortraitLoading,
      realDisplayAssets.length,
    ],
  );

  const virtualFilteredAssets = useMemo(
    () =>
      virtualMediaFilter === 'all'
        ? libDisplayAssets
        : libDisplayAssets.filter((a) => a.type === virtualMediaFilter),
    [libDisplayAssets, virtualMediaFilter],
  );

  const previewLibAsset = useCallback(
    (asset: Asset) => {
      const url =
        asset.type === 'video' || asset.type === 'audio'
          ? (asset.url || '').trim()
          : (asset.thumb || asset.url || '').trim();
      if (!url) {
        showToast(asset.type === 'audio' ? '暂无音频' : '暂无预览', 'e');
        return;
      }
      if (asset.type === 'video') {
        setModalVideoUrl(url);
      } else if (asset.type === 'audio') {
        setModalAudioTitle(asset.name);
        setModalAudioUrl(url);
      } else {
        setModalImageSrc(url);
      }
    },
    [setModalAudioTitle, setModalAudioUrl, setModalImageSrc, setModalVideoUrl, showToast],
  );

  const libAssetCenterActions = useCallback(
    (asset: Asset, onUse: () => void) => [
      {
        label: asset.type === 'audio' ? '播放' : '预览',
        onClick: () => previewLibAsset(asset),
      },
      {
        label: '使用素材',
        onClick: onUse,
      },
    ],
    [previewLibAsset],
  );

  const persistRealGroupId = useCallback((groupId: string) => {
    const id = groupId.trim();
    setRealGroupId(id);
    if (id) {
      setLocalStorageValue(SEEDANCE_STORAGE_KEYS.realPortraitGroupId, id);
    } else {
      removeLocalStorageValue(SEEDANCE_STORAGE_KEYS.realPortraitGroupId);
    }
  }, []);

  const realPersonApiOptions = useCallback(
    () => ({ onDebug: (msg: string, isError?: boolean) => addDebug(msg, isError) }),
    [addDebug],
  );

  const bindRealPersonGroupAction = useCallback(
    async (groupId: string) => {
      const id = groupId.trim();
      if (!id) throw new Error('缺少 GroupId');
      if (!hasTaskListConfig()) {
        showToast('请先配置系统访问令牌和用户 ID', 'e');
        openConfig();
        return;
      }
      setRealGroupBindBusy(true);
      try {
        await bindRealPersonGroupForUser(id, realPersonApiOptions());
        await realLib.refreshBoundGroups(id);
        showToast('真人认证并绑定成功', 's');
      } finally {
        setRealGroupBindBusy(false);
      }
    },
    [hasTaskListConfig, openConfig, realLib.refreshBoundGroups, realPersonApiOptions, showToast],
  );

  const stopRealAuthPolling = useCallback(() => {
    realAuthPollAbortRef.current?.abort();
    realAuthPollAbortRef.current = null;
  }, []);

  const runRealPortraitResultPoll = useCallback(
    async (bytedToken: string) => {
      stopRealAuthPolling();
      const controller = new AbortController();
      realAuthPollAbortRef.current = controller;
      setRealAuthBusy(true);
      setRealAuthStatus('正在查询认证结果…');
      try {
        const groupId = await pollRealPortraitGroupId(
          bytedToken,
          { onDebug: (msg, isError) => addDebug(msg, isError) },
          setRealAuthStatus,
          controller.signal,
        );
        persistRealGroupId(groupId);
        setRealAuthStatus('正在绑定真人素材组…');
        await bindRealPersonGroupAction(groupId);
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(REAL_PORTRAIT_PENDING_BYTED_TOKEN_KEY);
        }
        stripRealPortraitCallbackParams();
        setRealAuthStatus('');
      } catch (e: unknown) {
        if (isAbortLike(e)) return;
        const msg = e instanceof Error ? e.message : String(e);
        addDebug(`真人认证流程失败: ${msg}`, true);
        showToast('真人认证失败: ' + msg, 'e');
        setRealAuthStatus('');
      } finally {
        if (realAuthPollAbortRef.current === controller) {
          realAuthPollAbortRef.current = null;
        }
        setRealAuthBusy(false);
      }
    },
    [addDebug, bindRealPersonGroupAction, persistRealGroupId, showToast, stopRealAuthPolling],
  );

  const startRealPortraitAuth = useCallback(async () => {
    if (realAuthBusy) return;
    if (!hasTaskListConfig()) {
      showToast('请先配置系统访问令牌和用户 ID', 'e');
      openConfig();
      return;
    }
    stopRealAuthPolling();
    setRealAuthBusy(true);
    setRealAuthStatus('正在创建认证会话…');
    try {
      const { bytedToken, h5Link } = await startRealPortraitAuthSession({
        onDebug: (msg, isError) => addDebug(msg, isError),
      });
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(REAL_PORTRAIT_PENDING_BYTED_TOKEN_KEY, bytedToken);
      }
      window.open(h5Link, '_blank', 'noopener,noreferrer');
      setPortraitKind('real');
      void runRealPortraitResultPoll(bytedToken);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      addDebug(`发起真人认证失败: ${msg}`, true);
      showToast('发起真人认证失败: ' + msg, 'e');
      setRealAuthStatus('');
      setRealAuthBusy(false);
    }
  }, [
    addDebug,
    hasTaskListConfig,
    openConfig,
    realAuthBusy,
    runRealPortraitResultPoll,
    showToast,
    stopRealAuthPolling,
  ]);

  useEffect(() => {
    return () => stopRealAuthPolling();
  }, [stopRealAuthPolling]);

  const realAuthResumeHandledRef = useRef(false);
  useEffect(() => {
    if (realAuthResumeHandledRef.current) return;
    realAuthResumeHandledRef.current = true;

    const { bytedToken, resultCode } = parseRealPortraitCallbackParams();
    // 仅从 H5 回调 URL 恢复轮询；勿用 sessionStorage，避免从其他页进入真人人像时误显示「认证中」
    const callbackToken = bytedToken.trim();
    if (!callbackToken) {
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(REAL_PORTRAIT_PENDING_BYTED_TOKEN_KEY);
      }
      return;
    }

    if (resultCode && resultCode !== '10000') {
      showToast(`真人认证未成功（resultCode=${resultCode}）`, 'e');
      stripRealPortraitCallbackParams();
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(REAL_PORTRAIT_PENDING_BYTED_TOKEN_KEY);
      }
      return;
    }

    setPortraitKind('real');
    void runRealPortraitResultPoll(callbackToken);
  }, [runRealPortraitResultPoll, showToast]);

  const addRealMatFromLib = useCallback(
    (id: string) => {
      const asset = realDisplayAssets.find((x) => x.id === id);
      if (!asset) {
        showToast('找不到素材信息', 'e');
        return;
      }
      const { addMatNode, applyLibAssetToNode } = useSeedanceWorkspaceStore.getState();
      const resolvedAsset = resolveAssetForWorkspaceFromCache(
        asset,
        useSeedanceWorkspaceStore.getState().nodes,
      );
      void persistLibraryAssetPreviewCache([resolvedAsset]);
      const newNode = addMatNode(resolvedAsset.type);
      applyLibAssetToNode(newNode.id, resolvedAsset);
      void persistSeedanceWorkspaceState();
      showToast(`已添加${resolvedAsset.type === 'video' ? '视频' : resolvedAsset.type === 'audio' ? '音频' : '图片'}到工作台`, 's');
    },
    [realDisplayAssets, showToast],
  );

  return (
    <div className="lib-panel on" ref={libScrollRootRef}>
      <div className="lib-kind-tabs" role="tablist" aria-label="素材库类型">
        {(['virtual', 'real'] as const).map((kind) => (
          <button
            key={kind}
            type="button"
            role="tab"
            className={`lib-kind-tab${portraitKind === kind ? ' on' : ''}`}
            aria-selected={portraitKind === kind}
            onClick={() => setPortraitKind(kind)}
          >
            {LIBRARY_PORTRAIT_KIND_LABEL[kind]}
          </button>
        ))}
      </div>

      {portraitKind === 'real' ? (
        <div className="lib-content lib-content--real">
          <div className="lib-toolbar">
            <div className="lib-real-group-wrap">
              <div className="lib-real-group-field">
                <span className="lib-real-group-label" aria-hidden>
                  素材组
                </span>
                <select
                  id="lib-real-group-select"
                  className="lib-real-group-select"
                  value={realSelectedGroupId}
                  disabled={realGroupsLoading || realBoundGroupIds.length === 0}
                  onChange={(e) => handleRealGroupChange(e.target.value)}
                  aria-label="素材组"
                >
                  {realBoundGroupIds.length === 0 ? (
                    <option value="">暂无绑定素材组</option>
                  ) : (
                    realBoundGroupIds.map((id) => (
                      <option key={id} value={id}>
                        {id}
                      </option>
                    ))
                  )}
                </select>
              </div>
              {realSelectedGroupId ? (
                <span className="lib-group-id-count">
                  {realBootstrapLoading
                    ? '加载中…'
                    : realPortraitLoading
                      ? '刷新中…'
                      : realDisplayAssets.length
                        ? realPortraitHasMore
                          ? `已加载 ${realDisplayAssets.length} 个，继续向下滚动加载更多`
                          : `共 ${realDisplayAssets.length} 个素材`
                        : '暂无素材'}
                </span>
              ) : null}
            </div>
            <div className="lib-actions">
              <button
                type="button"
                className="btn refresh"
                onClick={() => reloadRealAssets()}
                title="刷新素材列表"
                disabled={!realSelectedGroupId || realPortraitLoading}
              >
                🔄 刷新列表
              </button>
              <button
                type="button"
                className="btn refresh lib-real-auth-btn"
                onClick={() => void startRealPortraitAuth()}
                disabled={realAuthBusy || realGroupBindBusy}
              >
                {realAuthBusy ? '认证中…' : '真人认证'}
              </button>
              <button type="button" className="btn refresh" onClick={openConfig} title="配置 Token">
                ⚙️ 配置
              </button>
            </div>
          </div>
          {realAuthStatus ? <p className="lib-real-status">{realAuthStatus}</p> : null}
          {realBoundGroupIds.length === 0 && !realGroupsLoading ? (
            <div className="lib-empty">
              <div className="icon">🧑</div>
              <div className="text">真人人像库</div>
              <p>暂无绑定的真人素材组，请先完成「真人认证」</p>
            </div>
          ) : realSelectedGroupId ? (
            realBootstrapLoading ? (
              <LibraryBootstrapPanel label="正在加载素材…" />
            ) : (
              <>
                <div className="lib-grid">
                  <button
                    type="button"
                    className="ac-root ac-root--compact lib-upload-slot"
                    disabled={realUploading}
                    onClick={() => realUploadInputRef.current?.click()}
                    aria-label={realUploading ? '正在上传素材' : '上传素材到当前素材组'}
                  >
                    <div className="ac-preview">
                      <span className="lib-upload-slot-icon" aria-hidden>
                        {realUploading ? '⏳' : '📤'}
                      </span>
                      <span className="lib-upload-slot-cta">
                        {realUploading ? '上传中…' : '上传素材'}
                      </span>
                    </div>
                    <div className="ac-info">
                      <div className="ac-title">添加至当前组</div>
                      <div className="ac-subtitle">支持多选素材</div>
                    </div>
                  </button>
                  {realDisplayAssets.map((asset) =>
                    asset.type === 'video' ? (
                      <VideoAssetCard
                        key={asset.id}
                        variant="compact"
                        videoUrl={asset.url}
                        poster={asset.thumb}
                        title={asset.name}
                        subtitle={`${ICONS[asset.type]} 视频`}
                        centerActions={libAssetCenterActions(asset, () => addRealMatFromLib(asset.id))}
                        deleteAction={{
                          onClick: () => void deleteRealAsset(asset),
                          busy: realDeletingAssetId === asset.id,
                        }}
                      />
                    ) : asset.type === 'audio' ? (
                      <AudioAssetCard
                        key={asset.id}
                        variant="compact"
                        audioUrl={asset.url}
                        title={asset.name}
                        subtitle={`${ICONS[asset.type]} 音频`}
                        centerActions={libAssetCenterActions(asset, () => addRealMatFromLib(asset.id))}
                        deleteAction={{
                          onClick: () => void deleteRealAsset(asset),
                          busy: realDeletingAssetId === asset.id,
                        }}
                      />
                    ) : (
                      <AssetCard
                        key={asset.id}
                        variant="compact"
                        src={asset.thumb || asset.url}
                        title={asset.name}
                        subtitle={`${ICONS[asset.type]} 图片`}
                        centerActions={libAssetCenterActions(asset, () => addRealMatFromLib(asset.id))}
                        deleteAction={{
                          onClick: () => void deleteRealAsset(asset),
                          busy: realDeletingAssetId === asset.id,
                        }}
                      />
                    ),
                  )}
                </div>
                <input
                  type="file"
                  multiple
                  accept={`image/*,video/*,.mp4,.mov,.webm,${LIBRARY_AUDIO_ACCEPT}`}
                  ref={realUploadInputRef}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    if (e.target.files) void uploadRealPortraitsToSelectedGroup(e.target);
                  }}
                />
                {!realBootstrapLoading && !realPortraitLoading && realDisplayAssets.length === 0 ? (
                  <p className="lib-empty-hint">当前组暂无素材，点击首张卡片上传</p>
                ) : null}
                {!realBootstrapLoading &&
                  (realDisplayAssets.length > 0 || realPortraitLoadingMore) ? (
                  <div
                    ref={realLoadMoreSentinelRef}
                    className="lib-feed-load-more"
                    role="status"
                    aria-live="polite"
                  >
                    {realPortraitLoadingMore ? (
                      <LibraryLoadingSpinner />
                    ) : realPortraitHasMore ? (
                      <span className="lib-feed-load-more-text">继续向下滚动加载更多</span>
                    ) : (
                      <span className="lib-feed-load-more-text">已加载全部素材</span>
                    )}
                  </div>
                ) : null}
              </>
            )
          ) : null}
        </div>
      ) : null}

      {portraitKind === 'virtual' ? (
        <>
          <div className="lib-toolbar">
            <div className="lib-real-group-wrap">
              <div className="lib-real-toolbar-row">
                {virtualGroupsLoading && virtualGroupOptions.length === 0 ? (
                  <span className="lib-segmented-hint">加载素材组…</span>
                ) : virtualGroupOptions.length === 0 ? (
                  <span className="lib-segmented-hint">暂无素材组</span>
                ) : virtualGroupOptions.length > 1 ? (
                  <div className="lib-segmented" role="group" aria-label="素材组">
                    {virtualGroupOptions.map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        className={`lib-segmented-btn${libSelectedGroupId === opt.groupId ? ' on' : ''}`}
                        title={opt.groupId}
                        disabled={virtualGroupsLoading}
                        onClick={() => handleVirtualGroupChange(opt.groupId)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                ) : null}
                <div className="lib-segmented" role="group" aria-label="素材类型">
                  {(['all', 'image', 'video', 'audio'] as const).map((kind) => (
                    <button
                      key={kind}
                      type="button"
                      className={`lib-segmented-btn${virtualMediaFilter === kind ? ' on' : ''}`}
                      onClick={() => setVirtualMediaFilter(kind)}
                    >
                      {LIBRARY_MEDIA_FILTER_LABEL[kind]}
                    </button>
                  ))}
                </div>
              </div>
              {libSelectedGroupId && (
                <span className="lib-group-id-count">
                  {virtualBootstrapLoading
                    ? '加载中…'
                    : libPortraitLoading
                      ? '刷新中…'
                      : virtualFilteredAssets.length
                        ? libPortraitHasMore
                          ? `${libPortraitSearchName.trim() ? `搜索「${libPortraitSearchName.trim()}」：` : ''}已加载 ${virtualFilteredAssets.length} 个${libraryMediaFilterCountLabel(virtualMediaFilter)}，继续向下滚动加载更多`
                          : `${libPortraitSearchName.trim() ? `搜索「${libPortraitSearchName.trim()}」：` : ''}共 ${virtualFilteredAssets.length} 个${libraryMediaFilterCountLabel(virtualMediaFilter)}`
                        : libPortraitSearchName.trim()
                          ? `未找到名称含「${libPortraitSearchName.trim()}」的${libraryMediaFilterCountLabel(virtualMediaFilter)}`
                          : libDisplayAssets.length
                            ? `当前组暂无${libraryMediaFilterCountLabel(virtualMediaFilter)}`
                            : '暂无素材'}
                </span>
              )}
            </div>

            {libSelectedGroupId ? (
              <div className="lib-name-search-field">
                <input
                  className="lib-name-search-input"
                  type="search"
                  placeholder="按素材名称搜索…"
                  value={nameSearchInput}
                  onChange={(e) => setNameSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      submitNameSearch();
                    }
                  }}
                  aria-label="按素材名称搜索"
                />
                {nameSearchInput.trim() ? (
                  <button
                    type="button"
                    className="lib-name-search-clear"
                    onClick={clearNameSearch}
                    aria-label="清除搜索"
                  >
                    ✕
                  </button>
                ) : null}
                <button
                  type="button"
                  className="lib-name-search-btn"
                  onClick={submitNameSearch}
                >
                  搜索
                </button>
              </div>
            ) : null}

            <div className="lib-actions">
              <button
                type="button"
                className="btn refresh"
                onClick={refreshAssets}
                title="刷新素材列表"
                disabled={!libSelectedGroupId || virtualGroupsLoading}
              >
                🔄 刷新列表
              </button>
              <input
                type="file"
                multiple
                accept={
                  virtualMediaFilter === 'video'
                    ? 'video/*,.mp4,.mov,.webm'
                    : virtualMediaFilter === 'image'
                      ? 'image/*,.png,.jpg,.jpeg,.webp,.gif'
                      : virtualMediaFilter === 'audio'
                        ? LIBRARY_AUDIO_ACCEPT
                        : `image/*,video/*,.mp4,.mov,.webm,${LIBRARY_AUDIO_ACCEPT}`
                }
                ref={uploadInputRef}
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files) uploadPortraitsToSelectedGroup(e.target);
                }}
              />
              <button type="button" className="btn refresh" onClick={openConfig} title="配置 Token">
                ⚙️ 配置
              </button>
            </div>
          </div>

          <div className="lib-content">
            {libSelectedGroupId ? (
              virtualBootstrapLoading ? (
                <LibraryBootstrapPanel
                  label={virtualGroupsLoading ? '正在加载素材组…' : '正在加载素材…'}
                />
              ) : (
                <>
                  <div className="lib-grid">
                    <button
                      type="button"
                      className="ac-root ac-root--compact lib-upload-slot"
                      disabled={libUploading}
                      onClick={() => uploadInputRef.current?.click()}
                      aria-label={libUploading ? '正在上传图片' : '上传图片到当前素材组'}
                    >
                      <div className="ac-preview">
                        <span className="lib-upload-slot-icon" aria-hidden>
                          {libUploading ? '⏳' : '📤'}
                        </span>
                        <span className="lib-upload-slot-cta">
                          {libUploading ? '上传中…' : '上传素材'}
                        </span>
                      </div>
                      <div className="ac-info">
                        <div className="ac-title">添加至当前组</div>
                        <div className="ac-subtitle">支持多选素材</div>
                      </div>
                    </button>
                    {virtualFilteredAssets.map((asset) =>
                      asset.type === 'video' ? (
                        <VideoAssetCard
                          key={asset.id}
                          variant="compact"
                          videoUrl={asset.url}
                          poster={asset.thumb}
                          title={asset.name}
                          subtitle={`${ICONS[asset.type]} 视频`}
                          centerActions={libAssetCenterActions(asset, () => addMatFromLib(asset.id))}
                          deleteAction={{
                            onClick: () => requestLibraryAssetDelete(asset),
                            busy: libDeletingAssetId === asset.id,
                          }}
                        />
                      ) : asset.type === 'audio' ? (
                        <AudioAssetCard
                          key={asset.id}
                          variant="compact"
                          audioUrl={asset.url}
                          title={asset.name}
                          subtitle={`${ICONS[asset.type]} 音频`}
                          centerActions={libAssetCenterActions(asset, () => addMatFromLib(asset.id))}
                          deleteAction={{
                            onClick: () => requestLibraryAssetDelete(asset),
                            busy: libDeletingAssetId === asset.id,
                          }}
                        />
                      ) : (
                        <AssetCard
                          key={asset.id}
                          variant="compact"
                          src={asset.thumb || asset.url}
                          title={asset.name}
                          subtitle={`${ICONS[asset.type]} 图片`}
                          centerActions={libAssetCenterActions(asset, () => addMatFromLib(asset.id))}
                          deleteAction={{
                            onClick: () => requestLibraryAssetDelete(asset),
                            busy: libDeletingAssetId === asset.id,
                          }}
                        />
                      ),
                    )}
                  </div>
                  {!libPortraitLoading && libDisplayAssets.length === 0 ? (
                    <p className="lib-empty-hint">当前组暂无素材，点击首张卡片上传</p>
                  ) : null}
                  {!libPortraitLoading &&
                    libDisplayAssets.length > 0 &&
                    virtualFilteredAssets.length === 0 ? (
                    <p className="lib-empty-hint">
                      当前组暂无{libraryMediaFilterCountLabel(virtualMediaFilter)}，可切换类型或上传
                    </p>
                  ) : null}
                  {!virtualBootstrapLoading &&
                    (libDisplayAssets.length > 0 || libPortraitLoadingMore) ? (
                    <div
                      ref={loadMoreSentinelRef}
                      className="lib-feed-load-more"
                      role="status"
                      aria-live="polite"
                    >
                      {libPortraitLoadingMore ? (
                        <LibraryLoadingSpinner />
                      ) : libPortraitHasMore &&
                        (virtualMediaFilter === 'all' || virtualFilteredAssets.length > 0) ? (
                        <span className="lib-feed-load-more-text">继续向下滚动加载更多</span>
                      ) : libPortraitHasMore ? null : (
                        <span className="lib-feed-load-more-text">已加载全部素材</span>
                      )}
                    </div>
                  ) : null}
                </>
              )
            ) : (
              <div className="lib-empty">
                <div className="icon">📦</div>
                <div className="text">
                  {virtualBootstrapLoading ? '正在加载素材组…' : '暂无可用素材组'}
                </div>
                <p>
                  {virtualBootstrapLoading
                    ? '请稍候'
                    : hasConfig
                      ? '请检查账号配置或点击「刷新列表」重试'
                      : '请先配置系统访问令牌和用户 ID'}
                </p>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default LibraryPage;
